'use client';

import type { QuoteSubmission } from '@/util/types/serverTypeCollection/repairList';
import { isQwikFixQuoteSubmission } from '@/util/functions/repairList/isQwikFixQuoteSubmission';
import QuoteEntryActionsDropdown from './QuoteEntryActionsDropdown';
import {
  buildQuoteActionItems,
  formatQuoteEntryDate,
  formatSubmissionType,
  getSubmissionPdfUrl,
  handleQuoteEntryAction,
  type QuoteActionKey,
} from './quoteEntryUtils';

const QWIKFIX_LOGO_SRC = '/brand/qwikfix-logo-dark.svg';

export default function QuoteSubmissionEntryRow({
  submission,
  repairListId,
  buildRepairListViewUrl,
  menuPortalContainer,
}: {
  submission: QuoteSubmission;
  repairListId: string;
  buildRepairListViewUrl: (repairListId: string, itemIds: string[]) => string;
  menuPortalContainer?: Element;
}) {
  const itemsCount = submission.includedItemIds?.length || 0;
  const businessName = submission.providerMetadata?.businessName;
  const categoryId = submission.categoryId?.trim();
  const isQwikFix = isQwikFixQuoteSubmission(submission);
  const dashboardUrl = submission.providerMetadata?.dashboardUrl;
  const qwikFixStatus = isQwikFix ? submission.providerMetadata?.qwikFixRequestStatus : undefined;
  const qwikFixStatusUpdatedAt = isQwikFix
    ? submission.providerMetadata?.qwikFixStatusUpdatedAt
    : undefined;
  const submissionPdfUrl = getSubmissionPdfUrl(submission);
  const thumbtackRequestPk =
    submission.providerMetadata?.thumbtackRequestCreatedEvent?.request_pk;
  const formattedCategoryName = categoryId ? categoryId.replace(/_/g, ' ') : '';

  const onAction = (key: QuoteActionKey) =>
    handleQuoteEntryAction({
      key,
      itemIds: submission.includedItemIds || [],
      pdfUrl: submissionPdfUrl,
      dashboardUrl,
      thumbtackRequestPk:
        submission.submissionType === 'thumbtack' ? thumbtackRequestPk : undefined,
      buildRepairListViewUrl,
      repairListId,
    });

  const renderSubmissionHeader = () => {
    if (isQwikFix) {
      return <img src={QWIKFIX_LOGO_SRC} alt='QwikFix' className='h-4 w-auto' />;
    }
    if (submission.submissionType === 'thumbtack') {
      return (
        <span className='flex items-center gap-2 text-sm font-medium capitalize text-gray-900'>
          {formattedCategoryName}
          <span className='text-gray-400'>-</span>
          <img
            src='https://thumbtack-brand.netlify.app/_next/static/media/logo.17900690.svg'
            alt='Thumbtack'
            className='h-4 w-auto'
          />
        </span>
      );
    }
    return (
      <span className='text-sm font-medium capitalize text-gray-900'>
        {formattedCategoryName
          ? `${formattedCategoryName} - ${formatSubmissionType(submission.submissionType)}`
          : formatSubmissionType(submission.submissionType)}
      </span>
    );
  };

  return (
    <div className='p-3'>
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <div className='mb-1 flex items-center gap-2'>{renderSubmissionHeader()}</div>
          {businessName && <div className='mb-1 text-sm text-gray-700'>{businessName}</div>}
          <div className='text-xs text-gray-600'>
            <span>
              {itemsCount} item{itemsCount !== 1 ? 's' : ''} submitted
            </span>
            <span className='mx-2'>•</span>
            <span>Requested on {formatQuoteEntryDate(submission.submittedAt)}</span>
          </div>
          {qwikFixStatus && (
            <div className='mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600'>
              <span className='rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 font-medium text-cyan-700'>
                {qwikFixStatus}
              </span>
              {qwikFixStatusUpdatedAt && (
                <span>Updated {formatQuoteEntryDate(qwikFixStatusUpdatedAt)}</span>
              )}
            </div>
          )}
        </div>
        <div className='ml-3 shrink-0'>
          <QuoteEntryActionsDropdown
            items={buildQuoteActionItems({
              pdfUrl: submissionPdfUrl,
              isQwikFix,
              dashboardUrl,
              thumbtackRequestPk:
                submission.submissionType === 'thumbtack' ? thumbtackRequestPk : undefined,
            })}
            onAction={onAction}
            portalContainer={menuPortalContainer}
          />
        </div>
      </div>
    </div>
  );
}
